import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { noop } from 'rxjs';
import { BaseButtonSize } from '../../../../shared/components/button/base-button.component';
import { BaseMimeType, BaseFileExtension } from '../../../../shared/enums/base-available-file-type.enum';
import { BaseModalService } from '../../../../shared/services/base-modal.service';
import { baseFilesizeValidator } from '../../../../shared/validators/base-filesize.validator';
import { baseFiletypeValidator } from '../../../../shared/validators/base-filetype.validator';
import { FormRawValue } from '../../../../modules/inquiry/_interfaces/base-form.interface';
import { DocumentFormInterface } from '../../../_interfaces/base-document-uploadmodal.interface';
import { ProfileDocumentResponse } from '../../../../../api';

@Component({
  selector: 'base-contract-upload',
  templateUrl: './base-contract-upload.component.html',
})
export class BaseContractUploadComponent implements OnInit {
  @ViewChild('contractUploadModal') contractUploadModal: ElementRef;
  @ViewChild('contractViewerModal') contractViewerModal: ElementRef;

  @Input() isContractUploadTurnedOff = false;
  @Input() disabledSwitchTooltip = '';
  @Output() enableContractUpload: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() uploadContract = new EventEmitter<File | ProfileDocumentResponse | null>();

  contractUploadForm: FormGroup<DocumentFormInterface>;
  initialFormValue: FormRawValue<FormGroup<DocumentFormInterface>>;
  isContractUploadFormSubmitted = false;
  isContractUploadEnabled = false;
  activeDocument: File | null;
  contractViewerModalRef: NgbModalRef;
  buttonSizes = BaseButtonSize;
  baseFileExtension = BaseFileExtension;

  constructor(private modalService: BaseModalService, private fb: NonNullableFormBuilder) {}

  ngOnInit() {
    this.initContractUploadForm();
  }

  onEnableContractUploadChange(event: boolean) {
    this.enableContractUpload.emit(event);
    if (!event) {
      this.resetContractUploadForm();
    }
  }

  openContractUploadModal() {
    this.modalService
      .open(
        {
          headText: 'document.add',
          bodyTemplate: this.contractUploadModal,
          okText: 'action.add',
          canBeClosed: this.canContractUploadModalBeClosed.bind(this),
        },
        {
          size: 'lg',
          backdrop: 'static',
        }
      )
      .result.then(
        () => {
          this.uploadContract.emit(this.contractUploadForm.getRawValue().file as File);
        },
        () => {
          this.resetContractUploadForm();
        }
      );
  }

  initContractUploadForm() {
    this.contractUploadForm = this.fb.group({
      name: this.fb.control<string>(''),
      category: this.fb.control<{ label: string; value: number } | null>(null),
      validUntil: this.fb.control<string | null>(null),
      private: this.fb.control<boolean>(false),
      file: this.fb.control<File | ProfileDocumentResponse | null>(null, [
        Validators.required,
        baseFilesizeValidator(),
        baseFiletypeValidator(new Set([BaseMimeType.pdf]), 'fileTypePDF'),
      ]),

      id: this.fb.control<string>(''),
    });

    this.initialFormValue = this.contractUploadForm.getRawValue();
  }

  viewContract(): void {
    const file = this.contractUploadForm.getRawValue().file as File;
    if (file) {
      this.openDocumentViewer(file as File);
    }
  }

  deleteUploadedContract(): void {
    this.uploadContract.emit(null);
    this.resetContractUploadForm();
  }

  private resetContractUploadForm(): void {
    this.contractUploadForm.reset(this.initialFormValue);
    this.contractUploadForm.markAsPristine();
  }

  private openDocumentViewer(document: File): void {
    if (!document) {
      return;
    }

    this.activeDocument = document;

    this.contractViewerModalRef = this.modalService.open(
      {
        bodyTemplate: this.contractViewerModal,
        cancelButtonDisplayed: false,
        okButtonDisplayed: false,
        hideHeader: true,
        hideFooter: true,
      },
      { size: 'xl' }
    );
    this.contractViewerModalRef.result.then(() => noop, noop);
  }

  private canContractUploadModalBeClosed(): boolean {
    this.isContractUploadFormSubmitted = true;
    return this.contractUploadForm.controls.file.valid;
  }
}
